import classNames from "classnames";
import { DateTime } from "luxon";
import { CSSProperties, Dispatch, FC, KeyboardEventHandler, LegacyRef, SetStateAction, useEffect, useRef, useState } from "react";
import renderGlobalAlert from "../../../system/hooks/useGlobalAlert";
import { DatePicker } from "../../datePicker/datePicker";
import styles from "./styles/BaseInput.module.scss";
import { MessageModalWindow } from "../../modalWindows/MessageModalWindow";
import { SearchButton } from "../../buttons/iconButtons/action/MainCollection";
import { useBarcodeReaderContext } from "../../../system/providers/barcodeReaderGlobal";
import { TimePicker } from "antd";
import Tooltip from "../../tooltips/Tooltip";
import BaseIconButton from "../../buttons/iconButtons/BaseIconButton";
import { MultiplyIcon } from "../../../libs/corporate/icons/outlined/suggested/SymbolCollection";
import { useTranslation } from "react-i18next";
import { MaxDateText, MinDateText } from "../../datePeriodPicker/datePeriodPicker";
import { DateStringsFormat } from "../../../hoc/DatePeriodPanel/DatePeriodPanel";
import { useSystemSettingsContext } from "../../../system/providers/systemSettingsProvider";
import { PopoverClue } from "../../popoverClue/PopoverClue";

// #region Customizing styles
type Variant = "normal" | "red" | "green" | "disabled" | "blue";
type LabelVariant = "normal" | "blue" | "green" | "required";

function getType(v?: Variant) {
    switch (v) {
        case "normal":
            return styles.lInputNormal;
        case "red":
            return styles.lInputRed;
        case "green":
            return styles.lInputGreen;
        case "disabled":
            return styles.lInputDisabled;
        default:
            return styles.lInputNormal;
    }
}

function getInlineType(v?: Variant) {
    switch (v) {
        case "normal":
            return styles.lInputNormalInline;
        case "red":
            return styles.lInputRedInline;
        case "green":
            return styles.lInputGreenInline;
        case "disabled":
            return styles.lInputDisabledInline;
        default:
            return styles.lInputNormalInline;
    }
}

function getLabelType(v?: LabelVariant) {
    switch (v) {
        case "normal":
            return styles.labelInputDefault;
        case "green":
            return styles.labelInputGreen;
        case "blue":
            return styles.labelInputBlue;
        case "required":
            return styles.labelInputRequired;

        default:
            return styles.labelInputDefault;
    }
}
// #endregion Customizing styles

interface IBaseInputProps {
    variant?: Variant;
    label?: string;
    inputId?: string;
    autocomplete?: string;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    labelClassName?: string;
    inputClassName?: string;
    inline?: boolean;
    labelVariant?: LabelVariant;
    error?: string;
    searchButtonShow?: boolean;
    required?: boolean;
    searchButtonOnClick?: () => void;
    maxTextLength?: number;
    clues?: string[];
    tabIndex?: number;
    'data-field-id'?: string;
}

export const BaseInput: FC<IBaseInputProps> = (props) => {
    const variant = props.disabled ? "disabled" : props.variant;
    const labelVariant = props.labelVariant === undefined ? "normal" : props.labelVariant;

    return (
        <>
            <div className={classNames(props.inline ? getInlineType(variant) : getType(variant), props.className)}>
                {props.label && (
                    <label className={classNames(props.labelClassName, getLabelType(labelVariant))} htmlFor={props.inputId}>
                        {props.required ? <strong>{`${props.label} *`}</strong> : props.label}
                    </label>
                )}
                <div className={styles.labelControl} style={{display : props.clues && 'flex'}}>
                    {props.children}
                    {
                        props.clues && (
                            <PopoverClue
                                clues={props.clues}
                            />
                        )
                    }
                    {props.error && <span className={styles.errorMessage}> {props.error} </span>}
                </div>
                {props.searchButtonShow && (
                    <SearchButton
                        onClick={() => {
                            props.searchButtonOnClick?.();
                        }}
                        sizeVariant={"normal"}
                    ></SearchButton>
                )}
            </div>
        </>
    );
};

interface IDefaultInput {
    variant?: Variant;
    label?: string;
    inputId?: string;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    inline?: boolean;
    labelVariant?: LabelVariant;
}

export const DefaultInput: FC<IDefaultInput> = (props) => {
    const variant = props.disabled ? "disabled" : props.variant;
    const labelVariant = props.labelVariant === undefined ? "normal" : props.labelVariant;

    return (
        <div className={classNames(props.inline ? getInlineType(variant) : getType(variant), props.className)}>
            {props.label && (
                <label className={classNames(getLabelType(labelVariant))} htmlFor={props.inputId}>
                    {props.label}
                </label>
            )}
            <div className={styles.labelControl}>{props.children}</div>
        </div>
    );
};

export interface ICurrencyInputValidation {
    warning?: {
        maxValue?: {
            max: number;
            name: string;
        };
        minValue?: {
            min: number;
            name: string;
        };
    };
    error?: {
        minValue?: {
            min: number;
            name: string;
        };
    };
}

interface ICurrencyInputWarning {
    name: string;
}
interface ICurrencyInputError {
    name: string;
}
interface ICurrencyInputWithValidation extends IBaseInputProps {
    value: number;
    style?: CSSProperties;
    onChange?: (value: number) => void;
    onEndChange?: (value: number) => void;//Завершение ввода без задержки
    onFocus?: () => void;
    onClick?: () => void;
    validation: ICurrencyInputValidation[] | null;
    min?: number;
    keyBan?: boolean;
}

const equalsKizCount = (kizCount: number | undefined, value: number): boolean => {
    if (!kizCount) return true;

    if (Number.isNaN(value) || kizCount > value) {
        renderGlobalAlert({
            variant: "warning",
            statusCode: 500,
            title: `Не возможно установить кол-во меньше чем отсканировано КИЗ/SSCC`,
        });
        return false;
    }
    return true;
};

const CurrencyInputWithValidation: FC<ICurrencyInputWithValidation> = (props) => {
    const [value, setValue] = useState<number>(props?.value ?? 0);
    const [inputValue, setInputValue] = useState<string>((props?.value ?? 0).toString());
    const [warning, setWarning] = useState<ICurrencyInputWarning | null>(null);

    const ref = useRef<HTMLInputElement>(null);
    const [focus, setFocus] = useState<boolean>(false);
    const [isInputFocused, setIsInputFocused] = useState<boolean>(false);

    useEffect(() => {
        if (!isInputFocused && props.value !== undefined && props.value !== value) {
            setValue(props.value);
            setInputValue(props.value.toString());
        }
    }, [props.value, value, isInputFocused]);

    useEffect(() => {
        if (!props.validation) return;

        props.validation.map((item) => {
            if (item.warning && item.warning.maxValue?.max) {
                if (value > item.warning.maxValue?.max) {
                    setWarning({ name: item.warning.maxValue.name });
                } else {
                    setWarning(null);
                }
            }
        });
    }, [value]);

    const currencyMasK = (n: number) => {
        if (n) {
            if (n > 1000000) {
                return value;
            } else {
                return Number(n.toFixed(2));
            }
        } else {
            return 0;
        }
    };

    useEffect(() => {}, [ref]);

    return (
        <BaseInput {...props}>
            {warning && focus && <label className={styles.popupLabel}>{warning.name}</label>}
            <input
                ref={ref}
                onMouseLeave={() => {
                    setFocus(false);
                }}
                onMouseMove={() => {
                    setFocus(true);
                }}
                disabled={props.disabled}
                type="number"
                className={props.error ? styles.inputError : props.inputClassName}
                onChange={(e) => {
                    const inputValue = e.currentTarget.value;

                    setInputValue(inputValue);

                    if (inputValue === "") {
                        setValue(0);
                        props.onChange?.(0);
                        return;
                    }

                    const value = e.currentTarget.valueAsNumber;
                    if (Number.isNaN(value)) return;

                    const maskedValue = currencyMasK(value);
                    setValue(maskedValue);
                    setInputValue(maskedValue.toString());
                    props.onChange?.(maskedValue);
                }}
                onBlur={(e) => {
                    setIsInputFocused(false);
                    let min = e.currentTarget.min;
                    let valueNumber = e.currentTarget.value === "" ? 0 : e.currentTarget.valueAsNumber;
                    let result = Math.max(Number(min), valueNumber);
                    if (Number.isNaN(result)) {
                        result = 0;
                    }
                    setValue(result);
                    setInputValue(result.toString());
                    // props.onChange?.(result);
                    // props.onEndChange?.(result);
                    if (result != valueNumber) {
                        renderGlobalAlert({
                            variant: "warning",
                            statusCode: 500,
                            title: `Не возможно установить кол-во меньше чем отсканировано КИЗ/SSCC`,
                        });
                    }
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    props.onClick?.();
                }}
                value={inputValue}
                tabIndex={props.tabIndex}
                data-field-id={props["data-field-id"]}
                min={props.min ?? 1}
                onFocus={(e) => {
                    setIsInputFocused(true);
                    props.onFocus?.();
                    e.currentTarget.select();
                }}
                onKeyDown={(event) => {
                    if (props.keyBan) {
                        const keyArr = [",", "."];
                        keyArr.includes(event.key) && event.preventDefault();
                    }

                    if (event.key === "Enter") {
                        props.onEndChange?.(value);
                        ref.current?.blur();
                    }

                    if (event.key === "Tab") props.onEndChange?.(value);

                    if (event.key === "Escape") {
                        event.currentTarget.blur();
                    }
                }}
            />
        </BaseInput>
    );
};

// #region <TextInput />
interface ITextInputProps extends IBaseInputProps {
    barcodeScan?: boolean;
    value?: string;
    ref?: LegacyRef<HTMLInputElement>;
    style?: CSSProperties;
    onKeyDown?: KeyboardEventHandler<any>;
    onFocus?: (e?: HTMLInputElement) => void;
    onChange?: (value: string) => void;
    onEndChange?: (value: string) => void;
    type?: string;
    required?: boolean;
    pattern?: string;
    autoFocus?: boolean;
    onClick?: () => void;
    numeric?: boolean;
    readonly?: boolean;
    isExternalValue?: boolean
    select?: [boolean, Dispatch<SetStateAction<boolean>>];
}
const TextInput: FC<ITextInputProps> = (props) => {

    const [value, setValue] = useState<string>(props?.value?? '');
    const textInput = useRef<HTMLInputElement>(null);
    const barcodeReaderContext = useBarcodeReaderContext();
    const searchHeight = useSystemSettingsContext().Search;

    useEffect(() => {
        setValue(props.value ?? '');
    }, [props.value]);
        
    useEffect(() => {
        let input = textInput.current as HTMLDivElement;
        if (props.autoFocus) {
            input?.focus();
            input.onkeydown = (e) => {
                switch (e.key) {
                    case "Enter": {
                        props?.onClick?.();
                        break;
                    }
                }
            };
        }
    }, [textInput, props.autoFocus]);

     useEffect(() => {
        if (props.select?.[0]) {
            textInput.current?.select()
            props.select?.[1](false)
        }
    }, [props.select]);

    useEffect(() => {
        if (props.barcodeScan && barcodeReaderContext.barcodeScanFlag === true) {
            textInput.current?.focus();
            setValue(barcodeReaderContext.barcode);
            props.onChange?.(barcodeReaderContext.barcode);
        } 
    }, [barcodeReaderContext.barcodeScanFlag]);

    function handleChange(e) {
        if (props.numeric) {
            const result = e.target.value.replace(/\D/gi, "");
            setValue(result)
            props.onChange?.(result)
        }
        else {
            setValue(e.currentTarget.value)
            props.onChange?.(e.currentTarget.value)
        }
    }

    return (
        <BaseInput {...props}>
            <input
                maxLength={props.maxTextLength ?? undefined}
                id={props.inputId}
                onClick={() => props.onClick?.()}
                ref={props.ref ?? textInput}
                type={props.type}
                style={{...props.style, height: props.searchButtonShow ? searchHeight : props.style?.height}}
                disabled={props.disabled}
                readOnly={props.readonly}
                className={props.error ? styles.inputError : props.inputClassName}
                value={props.isExternalValue ? props.value : value}
                required={props.required}
                pattern={props.pattern}
                autoComplete={props.autocomplete}
                placeholder={props.placeholder}
                onChange={handleChange}
                onBlur={(e) => props.onEndChange?.(e.currentTarget.value)}
                onKeyDown={(e) => {
                    props.onKeyDown?.(e);
                    if (e.key === 'Escape') {
                        e.currentTarget.blur();
                    }
                }}
                
                onFocus={(e) => props.onFocus?.(e.currentTarget)}
            />
        </BaseInput>
    );
};


// #endregion <TextInput />

// #region <NumberInput />
interface INumberInputProps extends IBaseInputProps {
    style?: CSSProperties;
    value?: number;
    defaultValue?: number;
    min?: number;
    max?: number;
    kizCount?: number;
    onClick?: () => void;
    onFocus?: () => void;
    onChange?: (value: number) => void;
    onEndChange?: (value: number) => void;
    propertyName?: string;
    numerator?: number;
    onBlur?: (value: number) => void;
    onEnter?: (value: number) => void;
    keyBan?: boolean;
    isEditKiz?: boolean;
    onKeyDown?: KeyboardEventHandler<any>;
    banEnterZerro?: boolean;
}
const NumberInput: FC<INumberInputProps> = (props) => {
    const [value, setValue] = useState<number>(props.value ?? 0);

    useEffect(() => setValue(props.value ?? 0), [props.value])
    useEffect(() => props.onChange?.(value), []);

    return (
        <>
            <BaseInput {...props}>
                <input
                    id={props.inputId}
                    disabled={props.disabled}
                    type="number"
                    onClick={(e) => {
                        e.stopPropagation();
                        props.onClick?.();
                    }}
                    style={props.style}
                    className={props.error ? styles.inputError : props.inputClassName}
                    value={value}
                    tabIndex={props.tabIndex}
                    data-field-id={props["data-field-id"]}
                    autoComplete={props.autocomplete}
                    placeholder={props.placeholder}
                    onChange={(e) => {
                        let { value, min, max } = e.currentTarget;
                        let finalValue;
                        if (value === "") {
                            finalValue = 0;
                        } else {
                            if (min === "" && max === "") {
                                finalValue = e.currentTarget.valueAsNumber;
                            } else if (min !== "" && max !== "") {
                                finalValue = Math.max(Number(min), Math.min(Number(max), e.currentTarget.valueAsNumber));
                            } else if (min !== "" && max === "") {
                                finalValue = Math.max(Number(min), e.currentTarget.valueAsNumber);
                            } else if (min === "" && max !== "") {
                                finalValue = Math.max(Number(max), e.currentTarget.valueAsNumber);
                            }
                        }
                        setValue(finalValue);
                        props.onChange?.(finalValue);
                    }}
                    onFocus={(e) => {
                        props.onFocus?.();
                        e.currentTarget.select();
                    }}
                    min={props.min}
                    max={props.max}
                    onBlur={(e) => {
                        props.onEnter?.(e.currentTarget.valueAsNumber);
                        props.onEndChange?.(e.currentTarget.valueAsNumber);
                    }}
                    onKeyDown={(event) => {
                        if (props.keyBan) {
                            const keyArr = [",", "."];
                            keyArr.includes(event.key) && event.preventDefault();
                        }

                        if (event.key === "Enter") {
                            props.onKeyDown?.(event)
                            props.onEnter?.(event.currentTarget.valueAsNumber);
                            props.onEndChange?.(event.currentTarget.valueAsNumber);
                        }

                        if (event.key === 'Escape') {
                            event.currentTarget.blur();
                        }
                    }}
                />
            </BaseInput>
        </>
    );
};

interface IMessageModalProps {
    show: boolean;
    value?: number;
}

const NumberInputWithSideEffect: FC<INumberInputProps> = (props) => {
    const [value, setValue] = useState<number>(props.value ?? 0);
    const [oldvalue, setOldValue] = useState<number>(props.value ?? 0);
    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({ show: false, value: undefined });
    const prevValueRef = useRef<number>();

    const isValueZero = (value: number): boolean => {
        if (props.isEditKiz === undefined) return true;

        if (value === 0) {
            renderGlobalAlert({
                variant: "warning",
                statusCode: 500,
                title: `Значение должно быть больше 0`,
            });
            setValue(oldvalue);
            props.onChange?.(oldvalue);
            props.onBlur?.(oldvalue);
            return false;
        }
        return true;
    };

    const changeScalingRatio = (value: number) => {
        if (!props.numerator) return;
        setShowMessageModal({ show: true, value: value });
    };

    const onBlur = (e): number | undefined => {
        if (props.propertyName !== "quantity") return undefined;

        let { min, max } = e.currentTarget;
        let valueNumber = e.currentTarget.valueAsNumber;

        if (min == "" && max == "") return valueNumber;
        else if (min != "" && max != "") return Math.max(Number(min), Math.min(Number(max), valueNumber));
        else if (min != "" && max == "") {
            let result = Math.max(Number(min), valueNumber);
            if (result != valueNumber) {
                renderGlobalAlert({
                    variant: "warning",
                    statusCode: 500,
                    title: `Не возможно установить кол-во меньше чем отсканировано КИЗ/SSCC`,
                });
            }
            return result;
        } else if (min == "" && max != "") return Math.max(Number(max), valueNumber);
    };

    useEffect(() => {
        prevValueRef.current = value;
    }, [value]);

    useEffect(() => {
        if (props.value !== undefined) {
            if (props.value !== value) {
                setValue(props.value as number);
            }
        }
    }, [props.value]);

    return (
        <>
            <BaseInput {...props}>
                <input
                    id={props.inputId}
                    disabled={props.disabled}
                    type="number"
                    onClick={(e) => {
                        e.stopPropagation();
                        props.onClick?.();
                    }}
                    style={props.style}
                    className={props.error ? styles.inputError : props.inputClassName}
                    value={value.toString()}
                    autoComplete={props.autocomplete}
                    placeholder={props.placeholder}
                    onChange={(e) => {
                        let value = 0 as number;
                        if (props.max) {
                            if (Number(e.currentTarget.value) > props.max) {
                                setValue(props.max);
                                props.onChange?.(props.max);
                            } else {
                                setValue(Number(e.currentTarget.value));
                                props.onChange?.(Number(e.currentTarget.value));
                            }
                        } else {
                            if (e.currentTarget.value == "") value = 0;
                            else value = e.currentTarget.valueAsNumber;
                            setValue(value);
                            props.onChange?.(value);
                        }
                    }}
                    onBlur={(e) => {
                        let result = onBlur(e);

                        if (props.banEnterZerro) {
                            const currentValue = result ?? e.currentTarget.valueAsNumber;
                            if (currentValue === 0) {
                                setValue(1);
                                props.onChange?.(1);
                                props.onBlur?.(1);
                                return;
                            }
                        }

                        if (result === undefined) return;

                        if (!isValueZero(result)) return;

                        if (props.numerator) {
                            changeScalingRatio(result);
                        } else {
                            setValue(result);
                        }

                        props.onBlur?.(result);
                        // props.onChange?.(result);
                        props.onEndChange?.(result);
                    }}
                    onFocus={(e) => {
                        props.onFocus?.();
                        e.currentTarget.select();
                    }}
                    min={props.min}
                    max={props.max}
                    onKeyDown={(event) => {
                        if (props.keyBan) {
                            const keyArr = [",", "."];
                            keyArr.includes(event.key) && event.preventDefault();
                        }

                        if (event.key === "Enter") {
                            props.onEnter?.(value);
                            props.onEndChange?.(value);
                            props.onKeyDown?.(event);
                        }

                        if (event.key === "Escape") {
                            event.currentTarget.blur();
                        }
                    }}
                />
            </BaseInput>

            {showMessageModal.show && (
                <MessageModalWindow
                    message={"Изменено количество партии в коробе. Пересчитать единицу измерения?"}
                    ok={{
                        onClick: () => {
                            setValue(showMessageModal.value ?? oldvalue);
                            props.onBlur?.(showMessageModal.value ?? oldvalue);
                            props.onChange?.(showMessageModal.value ?? oldvalue);
                            setOldValue(showMessageModal.value ?? oldvalue);
                            setShowMessageModal({ show: false, value: undefined });
                        },
                    }}
                    cancel={{
                        onClick: () => {
                            setValue(oldvalue);
                            props.onChange?.(oldvalue);
                            props.onBlur?.(oldvalue);
                            setShowMessageModal({ show: false, value: undefined });
                        },
                    }}
                    primary
                />
            )}
        </>
    );
};

// #endregion <NumberInput />

interface ICurrencyInput extends IBaseInputProps {
    value: number;
    min?: number;
    max?: number;
    style?: CSSProperties;
    onChange?: (value: number) => void;
    onEndChange?: (value: number) => void;
    onFocus?: () => void;
    onClick?: () => void;
    keyBan?: boolean;
}
const CurrencyInput: FC<ICurrencyInput> = (props) => {
    const [value, setValue] = useState<number>(props?.value ?? 0);

    const currencyMasK = (n: number) => {
        if (n) {
            if (n > 1000000) {
                return value;
            } else {
                setValue(Number(n.toFixed(2)));
                return Number(n.toFixed(2));
            }
        } else {
            return 0;
        }
    };

    useEffect(() => props.onChange?.(value), [value]);

    return (
        <BaseInput {...props}>
            <input
                disabled={props.disabled}
                type="number"
                onChange={(e) => {
                    setValue(currencyMasK(e.currentTarget.valueAsNumber));
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    props.onClick?.();
                }}
                value={value}
                min={props.min}
                max={props.max}
                onFocus={(e) => {
                    props.onFocus?.();
                    e.currentTarget.select();
                }}
                onBlur={(e) => props.onEndChange?.(value)}
                onKeyDown={(event) => {
                    if (props.keyBan) {
                        const keyArr = [",", "."];
                        keyArr.includes(event.key) && event.preventDefault();
                    }

                    if (event.key === "Enter") {
                        props.onEndChange?.(value);
                    }
                    
                    if (event.key === "Tab") props.onEndChange?.(value);

                    if (event.key === 'Escape') {
                        event.currentTarget.blur();
                    }
                }}
            />
        </BaseInput>
    );
};

// #region <TextAreaInput />
interface ITextAreaInputProps extends IBaseInputProps {
    value?: string;
    largeTextArea?: boolean;
    lineTextArea?: boolean;
    readonly?: boolean;
    onChange?: (value: string) => void;
    onEndChange?: (value: string) => void;
    style?: React.CSSProperties;
}

const TextAreaInput: FC<ITextAreaInputProps> = (props) => {
    const [value, setValue] = useState<string>(props.value ?? "");

    useEffect(() => {
        props.onChange?.(value);
    }, [value]);

    useEffect(() => {
        if (props.value) {
            setValue(props.value);
        }
    }, [props.value]);

    return (
        <>
            <BaseInput {...props} className={
                props.largeTextArea ?
                    styles.largeTextArea :
                    props.lineTextArea ? styles.lineTextArea : props.className
            }>
                <textarea
                    maxLength={props.maxTextLength ?? undefined}
                    id={props.inputId}
                    disabled={props.disabled}
                    style={props.style}
                    className={props.error ? styles.inputError : props.inputClassName}
                    value={value}
                    autoComplete={props.autocomplete}
                    placeholder={props.placeholder}
                    onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                            event.currentTarget.blur();
                        }
                    }}
                    onChange={(e) => {
                        props.readonly ?? setValue(e.currentTarget.value);
                    }}
                    onBlur={(e) => props.onEndChange?.(value)}
                />
            </BaseInput>
        </>
    );
};
// #endregion <TextAreaInput />

// #region <DateInput />
interface IDateInputProps extends IBaseInputProps {
    value?: DateTime | null;
    onBlur?: (value?: DateTime | null, dateString?: string) => void;
    onFocus?: () => void;
    isModal?: boolean;
    maxDate?: string;
    minDate?: string;
    preventFutureDates ?: boolean
    maxAllowedDate?: DateTime | null
}
const DateInput: FC<IDateInputProps> = (props) => {
    const [value, setValue] = useState<DateTime | null | undefined>(props.value);

    useEffect(() => {
        if(typeof value === "string") return;
        props.onBlur?.(value, value?.toFormat(DateStringsFormat));
    }, [value]);

    return (
        <>
            <BaseInput {...props}>
                <DatePicker
                    isModal={props.isModal}
                    date={props.value}
                    onBlur={(value) => {
                        if(typeof value === "string") return;
                        props.onBlur?.(value, value?.toFormat(DateStringsFormat))
                    }}
                    disabled={props.disabled}
                    minDate={props.minDate ? props.minDate : MinDateText}
                    maxDate={props.maxDate ? props.maxDate : MaxDateText}
                    onFocus={props.onFocus}
                    preventFutureDates ={props.preventFutureDates }
                    maxAllowedDate={props.maxAllowedDate}
                />
            </BaseInput>
        </>
    );
};

// #endregion <DateInput />

// #region <TimePickerInput />

interface ITimePickerInputProps extends IBaseInputProps {
    disabled?: boolean;
    format: 'HH:mm:ss' | 'HH:mm';
    value: moment.Moment;
    onChange: (value?: moment.Moment | null) => void;
}

const TimePickerInput: FC<ITimePickerInputProps> = (props) => {
    return (
        <>
            <BaseInput {...props}>
                <TimePicker
                    disabled={props.disabled}
                    format={props.format}
                    value={props.value}
                    onChange={props.onChange}
                    onSelect={props.onChange}
                    getPopupContainer={() => document.body}
                />
            </BaseInput>
        </>
    );
};

// #endregion <TimePickerInput />

const DecimalInput: FC<ITextInputProps> = (props) => {
    const textInput = useRef<HTMLInputElement>(null);
    useEffect(() => {
        let input = textInput.current as HTMLDivElement;
        if (props.autoFocus) {
            input?.focus();
            input.onkeydown = (e) => {
                switch (e.key) {
                    case "Enter": {
                        props?.onClick?.();
                        break;
                    }
                }
            };
        }
    }, [textInput, props.autoFocus]);

    const onChange = (e) => {
        let start = e.target.selectionStart;
        let val = e.target.value;
        val = val.replace(/([^0-9.]+)/, "");
        val = val.replace(/^(0|\.)/, "");
        const match = /(\d{0,7})[^.]*((?:\.\d{0,2})?)/g.exec(val);
        if (match !== null) {
            const value = match[1] + match[2];
            e.target.value = value;
            if (val.length > 0) {
                e.target.value = Number(value).toFixed(2);
                props.onChange?.(e.target.value);
                e.target.setSelectionRange(start, start);
            }
        }
    };

    return (
        <>
            <BaseInput {...props}>
                <input
                    id={props.inputId}
                    onClick={() => props.onClick?.()}
                    ref={props.ref ?? textInput}
                    type={props.type}
                    style={props.style}
                    disabled={props.disabled}
                    className={props.error ? styles.inputError : props.inputClassName}
                    value={props.value}
                    required={props.required}
                    pattern={props.pattern}
                    autoComplete={props.autocomplete}
                    placeholder={props.placeholder}
                    onChange={(e) => {
                        onChange(e);
                    }}
                    onKeyDown={(e) => {
                        props.onKeyDown?.(e);
                        if (e.key === "Escape") {
                            e.currentTarget.blur();
                        }
                    }}
                    onFocus={() => props.onFocus?.()}
                />
            </BaseInput>
        </>
    );
};

const formatsDecimalV2 = (val) => {
    let temp = String(val);

    temp = temp.replace(/[^\d.-]/g, "");
    temp = temp.replace(/^(0|\.)/, "");
    const match = /(\d{0,10})[^.]*((?:\.\d{0,2})?)/g.exec(temp);

    if (match !== null) {
        
        if (match[1] === '' || match[2] === '') var value = match[0];
        else value = match[1] + match[2];

        if (temp.length > 0) return Number(value).toFixed(2);
        return value || "0.00";
    }
    return String(val);
};

interface IDecimalInputProps extends IBaseInputProps {
    value?: number | string;
    ref?: LegacyRef<HTMLInputElement>;
    style?: CSSProperties;
    onKeyDown?: KeyboardEventHandler<any>;
    onFocus?: () => void;
    onBlur?: (value: number) => void;
    onEnter?: (value: number) => void; //Используется в ChangeableGrid для перехода по ячейкам.
    onEndChange?: (value: number) => void;//Завершение ввода без задержки
    onChange?: (value: number) => void;
    type?: string;
    required?: boolean;
    pattern?: string;
    autoFocus?: boolean;
    onClick?: () => void;
}

const DecimalInputV2: FC<IDecimalInputProps> = (props) => {
    const textInput = useRef<HTMLInputElement>(null);
    const [input, setInput] = useState(formatsDecimalV2(props.value ?? "0.00"));
    let start = 0;

    useEffect(() => {
        setInput(formatsDecimalV2(props.value ?? "0.00"));
    }, [props.value]);

    useEffect(() => {
        let input = textInput.current as HTMLDivElement;
        if (props.autoFocus) {
            input?.focus();
            input.onkeydown = (e) => {
                switch (e.key) {
                    case "Enter": {
                        props?.onClick?.();
                        break;
                    }
                }
            };
        }
    }, [textInput, props.autoFocus]);

    const handleChange = (e) => {
        start = e.target.selectionStart;
        let val = e.target.value;
        val = val.replace(/([^0-9.]+)/, "");
        val = val.replace(/^(0|\.)/, "");
        const match = /(\d{0,7})[^.]*((?:\.\d{0,2})?)/g.exec(val);

        if (match !== null) {
            const value = match[1] + match[2];
            e.target.value = value;
            setInput(value);
            if (val.length > 0) {
                e.target.value = Number(value).toFixed(2);
                e.target.setSelectionRange(start, start);
                setInput(Number(value).toFixed(2));
            }
            props.onChange?.(Number(value));
        }
    };

    return (
        <>
            <BaseInput {...props}>
                <input
                    id={props.inputId}
                    onClick={(e) => {
                        e.stopPropagation();
                        props.onClick?.();
                    }}
                    onDragStart={(e) => e.preventDefault()}
                    ref={textInput}
                    type={props.type}
                    style={props.style}
                    disabled={props.disabled}
                    className={props.error ? styles.inputError : props.inputClassName}
                    value={input}
                    tabIndex={props.tabIndex}
                    data-field-id={props["data-field-id"]}
                    required={props.required}
                    pattern={props.pattern}
                    autoComplete={props.autocomplete}
                    placeholder={props.placeholder}
                    onChange={handleChange}
                    onKeyDown={(event) => {
                        const inputKeyDown = textInput.current;

                        if ((event.ctrlKey || event.metaKey) && ["KeyC", "KeyV"].includes(event.code)) {
                            props.onKeyDown?.(event);
                            return;
                        }

                        if (inputKeyDown && inputKeyDown.value) {
                            if (inputKeyDown.selectionStart === 0 && inputKeyDown.selectionEnd === inputKeyDown.value.length) {
                                if (/[^0-9,.]/.test(event.key) && event.key !== "Tab" && event.key !== "Enter") {
                                    event.preventDefault();
                                    return;
                                }
                                if (/[.,0]/.test(event.key)) {
                                    event.preventDefault();
                                    setInput("0.00");
                                    props.onChange?.(0.0);
                                    setTimeout(() => inputKeyDown.setSelectionRange(inputKeyDown.value.indexOf(".") + 1, inputKeyDown.value.indexOf(".") + 1), 0);
                                }
                            } else if (/[,.]/.test(event.key)) {
                                event.preventDefault();
                                props.onChange?.(Number(input));
                                inputKeyDown.setSelectionRange(inputKeyDown.value.indexOf(".") + 1, inputKeyDown.value.indexOf(".") + 1);
                            }
                        }
                        if (event.key === "Enter") {
                            props.onEnter?.(Number(input));
                            props.onKeyDown?.(event);
                            props.onEndChange?.(Number(input));
                            // textInput.current?.blur();
                        }
                        
                        if (event.key === "Tab") props.onEndChange?.(Number(input));

                        if (inputKeyDown && inputKeyDown.selectionStart && event.key === 'Backspace') {
                            const cursorPosition = inputKeyDown.selectionStart;
                            const value = inputKeyDown.value;
    
                            // Получили позицию курсора и далее проверяем когда мы получим точку.
                            if (cursorPosition > 0 && value[cursorPosition - 1] === '.') {
                                // тут мы перемещаем курсор за точку тем самым предотвращаем удалением точки.
                                inputKeyDown.setSelectionRange(cursorPosition - 1, cursorPosition - 1);
                            }
                        }

                        if (event.key === "Escape") {
                            event.currentTarget.blur();
                        }
                    }}
                    onFocus={(e) => {
                        e.target.select();
                        props.onFocus?.();
                    }}
                    onBlur={() => {
                        props.onBlur?.(Number(input));
                        // props.onEndChange?.(Number(input));
                    }}
                />
            </BaseInput>
        </>
    );
};

interface IDecimalInputWithNegativesProps extends IBaseInputProps {
    value: number;
    ref?: LegacyRef<HTMLInputElement>;
    style?: CSSProperties;
    onKeyDown?: KeyboardEventHandler<any>;
    onFocus?: () => void;
    onBlur?: (value: number) => void;
    onEnter?: (value: number) => void;
    onEndChange?: (value: number) => void;
    onChange: (value: number) => void;
    type?: string;
    required?: boolean;
    pattern?: string;
    autoFocus?: boolean;
    onClick?: () => void;
}

const DecimalInputWithNegatives: FC<IDecimalInputWithNegativesProps> = ({
    value,
    onChange,
    ...props
}) => {
    const [inputValue, setInputValue] = useState(value.toString())
   
    useEffect(() => {
        setInputValue(value.toString());
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;        
        
        if (/^-?\d*\.?\d*$/.test(newValue)) {
            setInputValue(newValue);            
            
            if (newValue !== '-' && newValue !== '') {
                const num = parseFloat(newValue);
                onChange(num);
            }
        }
    };

    const handleBlur = () => {        
        const num = parseFloat(inputValue);
        if (!isNaN(num)) {
            onChange(num);
            setInputValue(num.toString());
        } else {            
            setInputValue(value.toString());
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
            return;
        }

        if (e.key === '.' || e.key === ',') {
            if (inputValue.includes('.')) {
                e.preventDefault();
            }
            if (e.key === ',') {
                e.preventDefault();
                const newValue = inputValue + '.';
                setInputValue(newValue);
            }
            return;
        }

        if (e.key === '-') {
            if (e.currentTarget.selectionStart !== 0) {
                e.preventDefault();
            }
            return;
        }

        if (!/\d/.test(e.key)) {
            e.preventDefault();
        }

        if (e.key === 'Enter') {
            handleBlur();
            e.currentTarget.blur();
        }

        if (e.key === 'Escape') {
            e.currentTarget.blur();
        }
    };

    return (
        <BaseInput {...props}>
            <input                
                type="text"
                // inputMode="decimal"
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                // onBlur={handleBlur}
                onFocus={(e) => {
                    e.target.select();
                    props.onFocus?.();
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    props.onClick?.();
                }}
                disabled={props.disabled}
                className={props.error ? styles.inputError : props.inputClassName}
                autoComplete={props.autocomplete}
                placeholder={props.placeholder || "0.00"}
            />
        </BaseInput>
    );
};

export function formatNumberWithTwoDecimalPlaces(number) {
  const formattedNumber = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
  return formattedNumber.replace(',', '.');
}

interface IMoneyFormatComponentProps extends IBaseInputProps{
    value: number | undefined;
    className?: string;
    style?: CSSProperties;
    getFormattedCurrencyData?: (value: string) => void;
    noInput?: boolean;
}

const MoneyFormatComponent: FC<IMoneyFormatComponentProps> = (props) => {
    const {
        value = 0,
        className,
        style,
        noInput,
        getFormattedCurrencyData
    } = props;
    
    const formattedNumber = formatNumberWithTwoDecimalPlaces(value);
    useEffect(() => {
        getFormattedCurrencyData && getFormattedCurrencyData(formattedNumber)
    }, [value])
    

    if (noInput) {
        return (
            <div
                className={className}
                style={style}
            >
                {formattedNumber}
            </div>
        );
    }

    return (
        <BaseInput {...props}>
            <input
                style={style}
                className={props.error ? styles.inputError : props.inputClassName}
                disabled
                value={formattedNumber}
            />
        </BaseInput>
    );
}

interface IDecimalStringInputProps extends IBaseInputProps {
    value?: string;
    onChange?: (value: string) => void;
}

export const DecimalStringInput = ({ value = '0,0', onChange, ...props }: IDecimalStringInputProps) => {
    const variant = props?.disabled ? "disabled" : props?.variant;
    const labelVariant = props?.labelVariant === undefined ? "normal" : props?.labelVariant;

    const [numberParts, setNumberParts] = useState<{ firstPart: string, secondPart: string }>({ firstPart: '0', secondPart: '0' });
    const secondPartRef = useRef<HTMLInputElement>(null);
    const firstPartRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const [firstPart, secondPart = '0'] = value.split(/[,./]/);
        setNumberParts({ firstPart: firstPart || '0', secondPart: secondPart });
    }, [value]);

    const handleInputChange = (e) => {
        const target = e.target;
        const value = target.value;

        let newFirstPart = numberParts.firstPart;
        let newSecondPart = numberParts.secondPart;

        if (target.name === 'firstPart') {
            newFirstPart = value.replace(/[^0-9]/g, '').slice(0, 10);
        } else if (target.name === 'secondPart') {
            newSecondPart = value.replace(/[^0-9]/g, '').slice(0, 11);
        }

        const updatedResult = `${newFirstPart}.${newSecondPart}`;
        setNumberParts({
            firstPart: newFirstPart,
            secondPart: newSecondPart
        });

        if (onChange) {
            onChange(updatedResult); // Передаем результат при каждом изменении
        }
    };

    const changeSetNumberParts = (name: string) => {
        const updatedParts = { ...numberParts, [name]: '0' };
        const updatedResult = `${updatedParts.firstPart}.${updatedParts.secondPart}`;
        setNumberParts(updatedParts);

        if (onChange) {
            onChange(updatedResult); // Передаем результат при изменении части
        }
    };

    const handleOnKeyDownChange = (e) => {
        const { name } = e.target;
        const selectionStart = e.target.selectionStart;
        const selectionEnd = e.target.selectionEnd;

        // Проверяем, если выделен весь текст
        const isAllSelected = selectionStart === 0 && selectionEnd === e.target.value.length;

        // Разрешаем перемещение фокуса стрелками
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Tab') {
            return; // Не блокируем стрелки и Tab
        }

        // Запрещаем ввод всех символов, кроме цифр
        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== '.' && e.key !== ',') {
            e.preventDefault(); // Предотвращаем ввод недопустимого символа
        }

        // Разрешаем ввод только цифр
        if (e.key === 'Backspace' || e.key === 'Delete') {
            // Если выделена вся строка или текст равен 1 символу, заменяем на '0'
            if (isAllSelected || e.target.value.length === 1) {
                e.preventDefault();

                if (name === 'firstPart' && numberParts.firstPart !== '0') {
                    changeSetNumberParts('firstPart');
                }
                if (name === 'secondPart' && numberParts.secondPart !== '0') {
                    changeSetNumberParts('secondPart');
                }
            }
        }

        // Перемещение фокуса на вторую часть числа при нажатии на точку или запятую
        if (e.key === '.' || e.key === ',') {
            e.preventDefault();
            if (secondPartRef.current) {
                secondPartRef.current.focus();
                secondPartRef.current.setSelectionRange(0, 0);
            }
        }
    };

    return (
        <>
            <div className={classNames(props.inline ? getInlineType(variant) : getType(variant), props.className)}>
                {props.label && (
                    <label className={classNames(props.labelClassName, getLabelType(labelVariant))} htmlFor={props.inputId}>
                        {props.required ? <strong>{`${props.label} *`}</strong> : props.label}
                    </label>
                )}
                <div className={styles.decimalStringInput}
                >
                    <input
                        type="text"
                        name="firstPart"
                        ref={firstPartRef}
                        value={numberParts.firstPart}
                        onChange={handleInputChange}
                        maxLength={10}
                        placeholder="1000000000"
                        onKeyDown={handleOnKeyDownChange}
                        className={styles.firstPart}
                    />
                    <span className={styles.separator}>.</span>
                    <input
                        type="text"
                        name="secondPart"
                        ref={secondPartRef}
                        value={numberParts.secondPart}
                        onChange={handleInputChange}
                        maxLength={11}
                        placeholder="00000000000"
                        onKeyDown={handleOnKeyDownChange}
                        className={styles.secondPart}
                    />
                </div>
            </div>
        </>
    );
};

interface IDecimalInputWithMinMaxProps extends IBaseInputProps {
    value?: number | string;
    min?: number;
    max?: number;
    maxDecimalPlaces?: number; // Количество знаков после запятой (по умолчанию 2)
    ref?: LegacyRef<HTMLInputElement>;
    style?: CSSProperties;
    onKeyDown?: KeyboardEventHandler<any>;
    onFocus?: () => void;
    onBlur?: (value: number) => void;
    onEnter?: (value: number) => void;
    onEndChange?: (value: number) => void;
    onChange?: (value: number) => void;
    autoFocus?: boolean;
    onClick?: () => void;
}

const DecimalInputWithMinMax: FC<IDecimalInputWithMinMaxProps> = (props) => {
    const textInput = useRef<HTMLInputElement>(null);
    const maxDecimals = props.maxDecimalPlaces ?? 2;
    const [input, setInput] = useState<string>(() => {
        const val = props.value ?? 0;
        return typeof val === 'number' ? String(val) : String(val);
    });
    let start = 0;

    useEffect(() => {
        const val = props.value ?? 0;
        setInput(String(val));
    }, [props.value]);

    useEffect(() => {
        let input = textInput.current as HTMLDivElement;
        if (props.autoFocus) {
            input?.focus();
            input.onkeydown = (e) => {
                switch (e.key) {
                    case "Enter": {
                        props?.onClick?.();
                        break;
                    }
                }
            };
        }
    }, [textInput, props.autoFocus]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        start = e.target.selectionStart ?? 0;
        let val = e.target.value;

        // Сохраняем знак минуса если есть
        const isNegative = val.startsWith('-');
        if (isNegative) {
            val = val.substring(1);
        }

        // Разрешаем только цифры и одну точку
        val = val.replace(/([^0-9.]+)/g, "");

        // Удаляем все точки кроме первой
        const firstDotIndex = val.indexOf('.');
        if (firstDotIndex !== -1) {
            const beforeDot = val.substring(0, firstDotIndex);
            const afterDot = val.substring(firstDotIndex + 1).replace(/\./g, '');
            val = beforeDot + '.' + afterDot;
        }

        // Разделяем на целую и дробную часть
        const parts = val.split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1] || '';

        // Ограничиваем количество знаков после запятой
        if (decimalPart.length > maxDecimals) {
            val = integerPart + '.' + decimalPart.substring(0, maxDecimals);
        }

        // Восстанавливаем знак минуса
        if (isNegative && val !== '') {
            val = '-' + val;
        }

        // Преобразуем в число для проверки min/max
        let numValue = val === '' || val === '.' || val === '-' || val === '-.' ? 0 : Number(val);

        // Применяем min/max ограничения и обновляем val
        if (props.min !== undefined && numValue < props.min) {
            numValue = props.min;
            val = String(props.min);
        }
        if (props.max !== undefined && numValue > props.max) {
            numValue = props.max;
            val = String(props.max);
        }

        setInput(val);
        props.onChange?.(isNaN(numValue) ? 0 : numValue);
    };

    const validateMinMax = (value: number): number => {
        let result = value;
        if (props.min !== undefined && value < props.min) {
            result = props.min;
        }
        if (props.max !== undefined && value > props.max) {
            result = props.max;
        }
        return result;
    };

    return (
        <>
            <BaseInput {...props}>
                <input
                    id={props.inputId}
                    onClick={(e) => {
                        e.stopPropagation();
                        props.onClick?.();
                    }}
                    onDragStart={(e) => e.preventDefault()}
                    ref={textInput}
                    type="text"
                    style={props.style}
                    disabled={props.disabled}
                    className={props.error ? styles.inputError : props.inputClassName}
                    value={input}
                    tabIndex={props.tabIndex}
                    data-field-id={props["data-field-id"]}
                    autoComplete={props.autocomplete}
                    placeholder={props.placeholder}
                    onChange={handleChange}
                    onKeyDown={(event) => {
                        const inputKeyDown = textInput.current;

                        // Автозамена запятой на точку
                        if (event.key === ',') {
                            event.preventDefault();
                            // Если уже есть точка, игнорируем запятую
                            if (input && input.includes('.')) {
                                return;
                            }
                            const start = inputKeyDown?.selectionStart ?? 0;
                            const end = inputKeyDown?.selectionEnd ?? 0;
                            const newValue = input.substring(0, start) + '.' + input.substring(end);
                            setInput(newValue);
                            const numValue = newValue === '' || newValue === '.' ? 0 : Number(newValue);
                            props.onChange?.(isNaN(numValue) ? 0 : numValue);
                            setTimeout(() => {
                                inputKeyDown?.setSelectionRange(start + 1, start + 1);
                            }, 0);
                            return;
                        }

                        // Блокируем ввод цифр, если уже достигнут лимит знаков после запятой
                        // Но не блокируем, если текст выделен (пользователь хочет заменить значение)
                        if (/^[0-9]$/.test(event.key)) {
                            const dotIndex = input.indexOf('.');
                            const isTextSelected = inputKeyDown?.selectionStart === 0 && 
                                                   inputKeyDown?.selectionEnd === input.length &&
                                                   input.length > 0;
                            // Если есть точка и после неё уже maxDecimals знаков и текст не выделен - блокируем
                            if (dotIndex !== -1 && input.length - dotIndex - 1 >= maxDecimals && !isTextSelected) {
                                event.preventDefault();
                            }
                        }

                        // Обработка клавиш при выделенном тексте
                        if (inputKeyDown && inputKeyDown.value) {
                            const isTextSelected = inputKeyDown.selectionStart === 0 && 
                                                   inputKeyDown.selectionEnd === inputKeyDown.value.length;
                            if (isTextSelected) {
                                // Когда всё выделено - разрешаем ввод цифр и разделителей, блокируем остальное
                                const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 
                                                     '.', ',', 'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
                                                     'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
                                if (!allowedKeys.includes(event.key) && !event.key.startsWith("Arrow")) {
                                    event.preventDefault();
                                    return;
                                }
                            }
                        }

                        if (event.key === "Enter") {
                            const numValue = Number(input) || 0;
                            const validatedValue = validateMinMax(numValue);
                            props.onEnter?.(validatedValue);
                            props.onKeyDown?.(event);
                            props.onEndChange?.(validatedValue);
                        }

                        if (event.key === "Tab") {
                            const numValue = Number(input) || 0;
                            const validatedValue = validateMinMax(numValue);
                            props.onEndChange?.(validatedValue);
                        }

                        if (inputKeyDown && inputKeyDown.selectionStart && event.key === 'Backspace') {
                            const cursorPosition = inputKeyDown.selectionStart;
                            const value = inputKeyDown.value;

                            if (cursorPosition > 0 && value[cursorPosition - 1] === '.') {
                                inputKeyDown.setSelectionRange(cursorPosition - 1, cursorPosition - 1);
                            }
                        }

                        if (event.key === "Escape") {
                            event.currentTarget.blur();
                        }
                    }}
                    onFocus={(e) => {
                        e.target.select();
                        props.onFocus?.();
                    }}
                    onBlur={() => {
                        let numValue = Number(input) || 0;
                        const validatedValue = validateMinMax(numValue);
                        setInput(validatedValue.toFixed(maxDecimals));
                        props.onBlur?.(validatedValue);
                        props.onEndChange?.(validatedValue);
                    }}
                />
            </BaseInput>
        </>
    );
};

export { TextInput, NumberInput, TextAreaInput, DateInput, TimePickerInput, CurrencyInput, NumberInputWithSideEffect, CurrencyInputWithValidation, DecimalInput, DecimalInputWithNegatives, DecimalInputV2, MoneyFormatComponent, DecimalInputWithMinMax };
